import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { keyframes } from 'tss-react';
import { useWindowState, useWindowManager } from './WindowManagerContext';
import { TASKBAR_H } from './Window';

// =============================================================================
// Taskbar — a fixed dock at the bottom of the page listing MINIMIZED windows.
// Click a tile to restore + focus it. Renders nothing when no window is
// minimized, so it never reserves space until needed. A window's
// options.attention (e.g. an unread IM) pulses its tile.
// =============================================================================

const attentionPulse = keyframes`
  0%, 100% { background: var(--wm-tile-bg); }
  50%      { background: var(--wm-tile-attention); }
`;

const useStyles = makeStyles()((theme) => ({
  // A normal-flow row between the chat messages and the composer (rendered there
  // by ChatPanel). Minimized IMs sit just above the input and push it down,
  // rather than overlaying the message list. Wraps to multiple rows if many are
  // minimized. Renders nothing (so reserves no space) when none are minimized.
  bar: {
    flexShrink: 0,
    minHeight: TASKBAR_H,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'stretch',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 0.75),
    background: theme.palette.custom.panelRaised,
    borderTop: `1px solid ${theme.palette.custom.line}`,
  },
  tile: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    alignSelf: 'center',
    maxWidth: 200,
    height: 26,
    padding: theme.spacing(0, 1.25),
    border: `1px solid ${theme.palette.custom.line}`,
    borderRadius: 6,
    background: theme.palette.custom.panel,
    color: theme.palette.custom.inkDim,
    fontFamily: theme.palette.custom.fontFamily,
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    transition: 'background 0.12s, color 0.12s',
    '--wm-tile-bg': theme.palette.custom.panel,
    '--wm-tile-attention': theme.palette.custom.ember,
    '&:hover': {
      color: theme.palette.custom.ink,
      background: theme.palette.custom.hoverOverlay,
    },
  },
  tileAttention: {
    animation: `${attentionPulse} 0.9s steps(1) infinite`,
    color: theme.palette.custom.ink,
  },
  label: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

const Taskbar = () => {
  const { classes, cx } = useStyles();
  const state = useWindowState();
  const { restoreWindow } = useWindowManager();

  const minimized = state.order
    .map((id) => state.windows[id])
    .filter((w) => w && w.minimized);

  if (minimized.length === 0) return null;

  return (
    <div className={classes.bar} role="toolbar" aria-label="Minimized windows">
      {minimized.map((w) => (
        <button
          key={w.id}
          type="button"
          className={cx(classes.tile, w.options?.attention && classes.tileAttention)}
          onClick={() => restoreWindow(w.id)}
          title={`Restore ${typeof w.title === 'string' ? w.title : 'window'}`}
        >
          <span className={classes.label}>{w.title || 'Window'}</span>
        </button>
      ))}
    </div>
  );
};

export default Taskbar;
